import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgencyFormData } from '../domain/entities/Agency.ts'
import { CreateAgencyUseCase } from '../domain/useCases/agencies/CreateAgencyUseCase.ts'
import { GetTourOperatorAgenciesUseCase } from '../domain/useCases/agencies/GetTourOperatorAgenciesUseCase.ts'
import { AgencyRepository } from '../repositories/AgencyRepository.ts'
import { AuthenticationRepository } from '../repositories/AuthenticationRepository.ts'

const agencyRepository = new AgencyRepository(new AuthenticationRepository())
const getTourOperatorAgenciesUseCase = new GetTourOperatorAgenciesUseCase(agencyRepository)
const createAgencyUseCase = new CreateAgencyUseCase(agencyRepository)

export const AGENCIES_QUERY_KEY = ['agencies'] as const

export function useAgencies() {
    const queryClient = useQueryClient()

    const agenciesQuery = useQuery({
        queryKey: AGENCIES_QUERY_KEY,
        queryFn: async () => {
            const agencies = await getTourOperatorAgenciesUseCase.execute()
            return agencies?.map((agency) => {
                const nameLowerCase = agency.name?.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
                return { ...agency, nameLowerCase }
            })
        },
    })

    const createAgencyMutation = useMutation({
        mutationFn: (data: AgencyFormData) => createAgencyUseCase.execute(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: AGENCIES_QUERY_KEY })
        },
    })

    return {
        agencies: agenciesQuery.data,
        isLoadingAgencies: agenciesQuery.isLoading,
        agenciesError: agenciesQuery.error,
        createAgency: createAgencyMutation.mutateAsync,
        isCreatingAgency: createAgencyMutation.isPending,
        createAgencyError: createAgencyMutation.error,
    }
}
